import {ChangeDetectorRef, Component, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {HttpParams} from '@angular/common/http';
import {NgbModal, NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';

import {SubCbDto, SubCbService} from './sub-cb-service';
import {SubCbModal} from './sub-cb-modal';
import {ConfirmDialogService} from '../../../shared/ui/confirm-dialog/confirm-dialog.service';
import {firstValueFrom} from 'rxjs';

import {resolveTotalElements} from '../../../shared/commons/pagination.util';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {StatusBadgeComponent} from '../../../shared/components/status-badge/status-badge.component';
import {SearchIconComponent} from '../../../shared/components/search-icon/search-icon.component';
import {DepartmentDto, DepartmentService} from '../department/department-service';

@Component({
  selector: 'app-sub-cb',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent, StatusBadgeComponent, SearchIconComponent],
  templateUrl: './sub-cb.html',
  styleUrl: './sub-cb.scss',
})
export class SubCbComponent implements OnInit {
  private readonly service = inject(SubCbService);
  private readonly departmentService = inject(DepartmentService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  protected readonly term = signal('');
  protected readonly stateFilter = signal<number | null>(null);
  protected readonly departmentFilter = signal<number | null>(null);
  protected readonly departments = signal<DepartmentDto[]>([]);
  protected readonly isLoading = signal(false);
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly collectionSize = signal(0);
  protected readonly displayedItems = signal<SubCbDto[]>([]);

  private totalElements = 0;

  ngOnInit(): void {
    this.load();
    this.loadDepartments();
  }

  private loadDepartments(): void {
    this.departmentService.getActiveDepartments().subscribe(departments => this.departments.set(departments));
  }

  load(): void {
    this.isLoading.set(true);
    let params = new HttpParams().set('page', this.page() - 1).set('size', this.pageSize());
    if (this.term()) {
      params = params.set('search', this.term());
    }
    if (this.stateFilter() != null) {
      params = params.set('state', this.stateFilter()!);
    }
    if (this.departmentFilter() != null) {
      params = params.set('departmentId', this.departmentFilter()!);
    }
    this.service.search(params).subscribe({
      next: (data) => {
        this.displayedItems.set(data.content);
        this.totalElements = resolveTotalElements(data);
        this.collectionSize.set(this.totalElements);
        this.isLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading.set(false);
        this.cdr.detectChanges();
      }
    });
  }

  onSearchChange(): void {
    this.page.set(1);
    this.load();
  }

  clearSearch(): void {
    this.term.set('');
    this.stateFilter.set(null);
    this.departmentFilter.set(null);
    this.onSearchChange();
  }

  openAddModal(): void {
    const modalRef = this.modalService.open(SubCbModal, {size: 'lg'});
    modalRef.componentInstance.onSave = async (result: SubCbDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed) {
        return false;
      }
      try {
        await firstValueFrom(this.service.createSubCb(result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openEditModal(item: SubCbDto): void {
    const modalRef = this.modalService.open(SubCbModal, {size: 'lg'});
    modalRef.componentInstance.subcb = item;
    modalRef.componentInstance.onSave = async (result: SubCbDto): Promise<boolean> => {
      const confirmed = await this.confirmDialogService.confirmSave();
      if (!confirmed || result.id == null) {
        return false;
      }
      try {
        await firstValueFrom(this.service.updateSubCb(result.id, result));
        this.load();
        return true;
      } catch {
        return false;
      }
    };
  }

  openViewModal(item: SubCbDto): void {
    const modalRef = this.modalService.open(SubCbModal, {size: 'lg'});
    modalRef.componentInstance.subcb = item;
    modalRef.componentInstance.viewOnly = true;
  }

  async delete(id: number): Promise<void> {
    const confirmed = await this.confirmDialogService.confirmDelete('คุณต้องการลบข้อมูลนี้ใช่หรือไม่?');
    if (!confirmed) {
      return;
    }
    this.service.deleteSubCb(id).subscribe(() => this.load());
  }
}
